import { Typo } from '@geberit/gdds';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// types
import type { AnyAction } from 'redux';
import type { BasketItem } from 'components/ShoppingBasket/reducer.types';

// styles
import styles from './summary-container.module.scss';

// components
import { Translation } from 'components/Translation/Translation';
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import { Loader } from 'components/Loader/Loader';
import { SummaryData } from './summary-data';
import LinkWrapper from 'components/checkout/components/link-wrapper/link-wrapper';
import ErrorMessageBox from 'components/checkout/components/error-message-box/error-message-box';

// utils
import { fetchOrderDataAction } from 'components/checkout/actions';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { isEmpty } from 'utils/is-empty';
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';
import { useTracking } from 'utils/tracking/track';
import { roundValueTwoDigits } from 'utils/round-value';
import { safeJsonParse } from 'components/checkout/components/address-form/utils';
import { useXy } from 'utils/hooks/use-xy';
import {
  orderDataSelector,
  isOrderDataFetchingSelector,
  orderDataErrorSelector,
} from 'components/checkout/selectors';

export default function SummaryContainer() {
  const dispatch = useDispatch();
  const translate = useTranslationFunction();
  const orderData = useSelector(orderDataSelector);
  const isOrderDataFetching = useSelector(isOrderDataFetchingSelector);
  const orderError = useSelector(orderDataErrorSelector);
  const { trackWebshop } = useTracking();
  const isXy = useXy();

  const urlParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : '',
  );

  const orderId = urlParams.get('orderId');
  const locale = urlParams.get('locale');

  useEffect(() => {
    if (orderId && locale) dispatch(fetchOrderDataAction(locale, orderId) as unknown as AnyAction);
  }, [dispatch, locale, orderId]);

  useEffect(() => {
    if (!isEmpty(orderData) && orderId) {
      const checkoutSummary = localStorage.getItem(orderId);
      if (checkoutSummary) {
        const parsedCheckoutSummary = safeJsonParse(checkoutSummary, {
          voucherCode: '',
          basketItems: [] as BasketItem[],
        });
        trackWebshop('purchase', {
          transaction_id: orderId,
          value: roundValueTwoDigits(orderData.totalPriceVatIncluding),
          tax: roundValueTwoDigits(orderData.vatAmount),
          coupon: parsedCheckoutSummary?.voucherCode ?? '',
          shipping: roundValueTwoDigits(orderData.postalCostVatIncluding),
          items: (orderData.lineItemsList ?? []).map((item, index) => ({
            item_id: item.sku,
            item_name: item.description,
            quantity: item.quantity,
            price: roundValueTwoDigits(
              parsedCheckoutSummary?.basketItems?.find((basketItem) => basketItem.sku === item.sku)
                ?.pricePerItem,
            ),
            discount: roundValueTwoDigits(item.priceVoucherPcs),
            index,
          })),
        });
        localStorage.removeItem(orderId);
      }
    }
  }, [orderData, orderId]);

  if (isOrderDataFetching || (isEmpty(orderData) && !orderError)) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader size="medium" />
      </div>
    );
  }

  const summaryDescription = (
    <>
      <Translation id="web20_checkout_summary_description_part1" />{' '}
      <LinkWrapper translationLink={translate('web20_checkout_link_customer_service')} />
      {'. '}
      <Translation id="web20_checkout_summary_description_part3" />
    </>
  );

  const summaryQuestion = (
    <>
      <Translation id="web20_checkout_summary_questions_part1" />{' '}
      <LinkWrapper translationLink={translate('web20_checkout_link_frequently_asked_questions')} />{' '}
      <Translation id="web20_checkout_summary_questions_part3" />
    </>
  );

  let content;
  if (orderError || !orderId || !locale) {
    content = (
      <ErrorMessageBox>
        {'730 - '}
        <Translation id="web20_checkout_summary_error" />{' '}
        <LinkWrapper translationLink={translate('web20_checkout_link_customer_service')} />
      </ErrorMessageBox>
    );
  } else if (orderData) {
    content = <SummaryData orderId={orderId} orderData={orderData} />;
  } else {
    content = null;
  }

  return (
    <div>
      {isXy ? (
        <>
          <Headline
            tag={Formats.h1}
            className={styles.bottomMargin}
            title={translate('web20_checkout_summary_heading_part1')}
            subtitle={translate('web20_checkout_summary_heading_part2')}
          />
          <Typo tag="p">{summaryDescription}</Typo>
          <Typo tag="p">{summaryQuestion}</Typo>
        </>
      ) : (
        <>
          <h1 className={styles.h1}>
            <span>
              <Translation id="web20_checkout_summary_heading_part1" />
            </span>{' '}
            <Translation id="web20_checkout_summary_heading_part2" />
          </h1>
          <p>{summaryDescription}</p>
          <p>{summaryQuestion}</p>
        </>
      )}
      {content}
    </div>
  );
}
